import { Button, Form, Input, message, Modal, Select } from 'antd'
import { useEffect } from 'react'
import handle from 'services/Axios'
import SWR_KEY from 'services/swr/constant'
import { useBankList } from 'services/swr/payment'
import { useModalStore } from 'state/useModalStore'
import { PaymentType, usePaymentStore } from 'state/usePaymentStore'
import useSWRMutation from 'swr/mutation'
import { useTranslation } from 'translations'

const ModalPayment = () => {
	const [form] = Form.useForm()
	const { t } = useTranslation()

	const addPayment = usePaymentStore((s) => s.addPayment)
	const updatePayment = usePaymentStore((s) => s.updatePayment)
	const { open, modalData } = useModalStore((s) => s.modalPayment)

	const setModalPayment = useModalStore((s) => s.setModalPayment)
	const modalTitle = modalData?.type === "CREATE" ? "Add Recipients" : "Update Recipients"

	const { data: bankData } = useBankList()

	/**
	 * Functions
	 * ============================================================================
	 */

	const onCancel = () => {
		form.resetFields()
		setModalPayment({ open: false, modalData: null })
	}

	const { trigger: createPayment, isMutating: loadingCreate } = useSWRMutation(
		SWR_KEY.CREATE_PAYMENT,
		async (url, { arg }) => {
			try {
				const res = await handle.post(url, arg)
				const resData = res.data.data as PaymentType

				addPayment({
					id: resData.id,
					name: resData.name,
					user_id: resData.user_id,
					account_number: resData.account_number,
					account_holder: resData.account_holder,
					bank_id: resData.bank_id,
				})

				form.resetFields()
				message.success("Create Successfully")
				onCancel()
				return res
			} catch (error: any) {
				message.error(error.message)
			}
			return
		},
	)

	const { trigger: editPayment, isMutating: loadingUpdate } = useSWRMutation(
		[SWR_KEY.UPDATE_PAYMENT, modalData?.data?.id],
		async ([url, id], { arg }) => {
			try {
				const res = await handle.post(url + '/' + id, arg)
				const resData = res.data.data as PaymentType

				updatePayment({
					id: resData.id,
					name: resData.name,
					user_id: resData.user_id,
					account_number: resData.account_number,
					account_holder: resData.account_holder,
					bank_id: resData.bank_id,
				})

				message.success("Update Successfully")
				onCancel()
				return res
			} catch (error: any) {
				message.error(error.message)
			}
			return
		},
	)

	const handleSubmit = () => {
		if (!bankData) return

		form.validateFields().then((values) => {
			const payload = {
				...values,
				name: bankData.find(b => b.bin === values.bank_id)?.name,
			}

			if (modalData?.data?.id) editPayment(payload)
			else createPayment(payload)
		})
	}

	/**
	 * Effects
	 * ============================================================================
	 */

	useEffect(() => {
		if (!modalData?.data) return
		const { bank_id, account_number, account_holder } = modalData.data
		form.setFieldsValue({ bank_id, account_number, account_holder })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalData?.data?.id])

	return (
		<Modal
			centered
			open={open}
			footer={false}
			onCancel={onCancel}
			title={modalTitle}
			destroyOnClose
		>
			<Form form={form} layout="vertical">
				<Form.Item
					label={t("Bank name")}
					name="bank_id"
					rules={[{ required: true, message: t('Name is require') }]}
				>
					<Select
						options={bankData?.map(b => ({ label: (b.bankCode || b.code) + ' - ' + b.name, value: b.bin }))}
						placeholder={t("Bank name")}
						loading={!bankData}
					/>
				</Form.Item>

				<Form.Item
					label={t("Account number")}
					name="account_number"
					rules={[{ required: true, message: t('Account number is require') }]}
				>
					<Input placeholder={t("Account number")} />
				</Form.Item>

				<Form.Item
					label={t("Account holder")}
					name="account_holder"
					rules={[{ required: true, message: t('Account holder is require') }]}
				>
					<Input placeholder={t("Account holder")} />
				</Form.Item>
			</Form>

			<Button type="primary" size='large' className='w-full' onClick={handleSubmit} loading={loadingCreate || loadingUpdate}>
				{t(modalData?.data?.id ? t("Update") : t("Create"))}
			</Button>
		</Modal>
	)
}

export default ModalPayment
